import {
    Box,
    Chip,
    CircularProgress,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import { Search } from 'lucide-react';
import { useIntl } from 'react-intl';
import type { UsePacsImport } from '../hooks';

/**
 * The lookup band: which sources to ask, and the one term to ask them by.
 *
 * One field, not two. The connector matches the term against the patient id exactly and against the
 * name as a prefix, and unions the two, so a user holding either types it in the one place. The
 * lookup runs as the user types once the term is long enough to be selective, and Enter runs it at
 * once whatever its length, so a short id is never unreachable.
 *
 * The asymmetry of the match is stated, not left to be inferred. It is a safety decision: a widened
 * id match is how a lookup offers the wrong patient, while a widened name only lengthens a list the
 * user then picks from by reading the identity beside each row. The statement lives on the screens
 * that carry no result to read, the empty state and the no-match answer, rather than under this row.
 *
 * Every configured source is shown and each is included or excluded in place. An excluded source is
 * still listed, so a search that finds nothing cannot be mistaken for a search of everywhere.
 */

/**
 * Reachability, stated as what it is. A source that answered a verification request is not thereby
 * guaranteed to hold anything, so the label says "reachable" rather than "available", and an
 * unreachable source stays includable: the user may know more about the network than the last probe.
 *
 * The dot carries the state alone, so it names itself: colour is its only visual carrier.
 */
function TargetDot({ isReachable }: { isReachable: boolean }) {
    const intl = useIntl();
    return (
        <Box
            component="span"
            role="img"
            aria-label={intl.formatMessage({
                id: isReachable
                    ? 'patientList.import.pacs.reachable'
                    : 'patientList.import.pacs.notReachable',
            })}
            sx={{
                width: 6,
                height: 6,
                flexShrink: 0,
                borderRadius: 1,
                bgcolor: isReachable ? 'success.main' : 'error.main',
            }}
        />
    );
}

function SourceChips({ pacs }: { pacs: UsePacsImport }) {
    const intl = useIntl();
    const { state, targets, actions } = pacs;
    // Sources are fixed for the lifetime of a lookup's results only while nothing else is happening:
    // changing them mid-retrieval would change nothing already asked for, and would read as if it did.
    const locked = state.stage.kind !== 'search';
    if (pacs.isLoadingTargets || targets.length === 0) {
        return (
            <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                {intl.formatMessage({
                    id: pacs.isLoadingTargets
                        ? 'patientList.import.pacs.readingSources'
                        : 'patientList.import.pacs.noSourceConfigured',
                })}
            </Typography>
        );
    }
    return (
        <Box
            role="group"
            aria-label={intl.formatMessage({
                id: 'patientList.import.pacs.sourcesLabel',
            })}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}
        >
            {targets.map((target) => {
                const included = state.sources.includes(target.name);
                return (
                    <Chip
                        key={target.name}
                        size="small"
                        variant={included ? 'filled' : 'outlined'}
                        color={included ? 'primary' : 'default'}
                        disabled={locked}
                        onClick={() => actions.toggleSource(target.name)}
                        aria-pressed={included}
                        icon={<TargetDot isReachable={target.isReachable} />}
                        label={target.name}
                        sx={{
                            // The dot sits where MUI puts an icon; its default margins are sized
                            // for an 18px glyph.
                            '& .MuiChip-icon': { ml: 1, mr: -0.25 },
                            ...(included
                                ? {}
                                : {
                                      color: 'text.secondary',
                                      textDecoration: 'line-through',
                                  }),
                        }}
                    />
                );
            })}
        </Box>
    );
}

export default function PacsLookupForm({ pacs }: { pacs: UsePacsImport }) {
    const intl = useIntl();
    const { state, actions } = pacs;
    const searchLabel = intl.formatMessage({
        id: 'patientList.import.pacs.searchLabel',
    });
    const isSearching =
        state.lookup !== undefined &&
        Object.values(state.lookup.bySource).some(
            (search) => search.status === 'searching'
        );

    return (
        // A real form, so Enter runs the lookup from the field at once. Submitting with nothing to
        // ask is harmless: the action refuses it.
        <Box
            component="form"
            onSubmit={(event) => {
                event.preventDefault();
                actions.lookUp();
            }}
            sx={{
                flex: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                padding: '12px 16px',
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <TextField
                // Focused on arrival: looking a patient up is what the dialog opens for.
                autoFocus
                value={state.search}
                onChange={(event) => actions.setSearch(event.target.value)}
                placeholder={searchLabel}
                disabled={state.stage.kind !== 'search'}
                // The term is a patient identifier; browser form history must not store it and
                // offer it to the next user of a shared workstation.
                inputProps={{
                    'aria-label': searchLabel,
                    autoComplete: 'off',
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search size={14} />
                        </InputAdornment>
                    ),
                    endAdornment: isSearching ? (
                        <InputAdornment position="end">
                            <CircularProgress
                                size={12}
                                aria-label={intl.formatMessage({
                                    id: 'patientList.import.pacs.lookingUp',
                                })}
                            />
                        </InputAdornment>
                    ) : undefined,
                }}
                fullWidth
            />
            <SourceChips pacs={pacs} />
        </Box>
    );
}
